import { type FC, useEffect, useRef } from 'react';

import { useStore } from '@nanostores/react';

import { cx, type WatermarkOptions } from '@iconik-io/unified-player-core';

import { useBasePlayerContext } from '../../hooks/use_player_context/use_base_player_context.js';

import sizeStyles from '../../styles/player_aware_size.module.css';

export const Watermark: FC = () => {
  const { store } = useBasePlayerContext();
  const watermarks = useStore(store.$watermarks);
  const watermarkOptions = useStore(store.$watermarkOptions);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contentSize = useStore(store.size.$naturalSize);
  const contentType = useStore(
    store.playlist.$currentPlaylistItem,
  )?.contentType;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (ctx && canvas) {
      canvas.width = contentSize.width;
      canvas.height = contentSize.height;
      const textSize = Math.max(8, Math.round(contentSize.width * 0.024));
      const { textOpacity, shadowOpacity, position } = watermarkOptions;
      ctx.font = `600 ${textSize}px Inter`;
      ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(255, 255, 255, ${textOpacity})`;
      ctx.shadowColor = `rgba(0, 0, 0, ${shadowOpacity})`;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      ctx.shadowBlur = 2;

      const lineHeight = textSize * 1.4;
      const textBlockHeight = (watermarks.length - 1) * lineHeight;

      const yStart = setVerticalAlignment(
        ctx,
        contentSize.height,
        textBlockHeight,
        position,
      );

      let y = yStart;

      watermarks.forEach((watermark) => {
        ctx.fillText(watermark, contentSize.width / 2, y);
        y += lineHeight;
      });
    }
  }, [contentSize, watermarkOptions, watermarks]);

  if (
    (contentType !== 'video' &&
      contentType !== 'image' &&
      contentType !== 'pdf') ||
    !watermarks.length
  ) {
    return null;
  }
  return (
    <canvas
      ref={canvasRef}
      className={cx([
        sizeStyles.player,
        sizeStyles.transforming_player,
        sizeStyles.positioned_above,
      ])}
    />
  );
};

function setVerticalAlignment(
  context: CanvasRenderingContext2D,
  canvasHeight: number,
  textBlockHeight: number,
  position: WatermarkOptions['position'],
): number {
  const borderDistance = 5;
  if (position === 'top') {
    context.textBaseline = 'top';
    return (borderDistance / 100) * canvasHeight;
  }
  if (position === 'bottom') {
    context.textBaseline = 'bottom';
    return (
      canvasHeight - (borderDistance / 100) * canvasHeight - textBlockHeight
    );
  }
  context.textBaseline = 'middle';
  return canvasHeight / 2 - textBlockHeight / 2;
}
